import {
  addDays,
  adjustToTimeZone,
  DispatchRequestEntity,
  dispatchRequestsSelector,
  endOfDay,
  extractApiError,
  getObjectId,
  startOfDay,
  subDays,
  useDispatchRequestPrefetch,
  useSearchDispatchRequestsQuery
} from "@ots-frontend/ots-data";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DispatchRequestToolbar} from "../../components/DispatchRequestToolbar/DispatchRequestToolbar";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {getAppDttm} from "../../store/ui/selectors";
import {useDispatch} from "react-redux";
import {setAppDate} from "../../store/ui/actions";
import {Box, Card, Container, Flex, Group, LoadingOverlay, Menu, rem, Text, Title, UnstyledButton} from "@mantine/core";
import {
  CommentModal,
  DispatchRequestDataGrid,
  DispatchRequestDispatchAction,
  DispatchRequestFilterFormModal,
  DispatchRequestFilterFormValues,
  GridSort,
  ResizeableContent,
  ThemedPage,
  useApp,
  useDispatchRequestMutation
} from "@ots-frontend/ots-ui";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useDisclosure} from "@mantine/hooks";
import {useNavigate} from "react-router-dom";
import {NavHeaderIsland} from "../../components/NavHeaderIsland/NavHeaderIsland";
import {getDispatchRequestPageFilter, getDispatchRequestPageState} from "../../store/dispatch-request-page/selectors";
import {CompactSelection, DataEditorRef, GridSelection, Item} from "@glideapps/glide-data-grid";
import {CellClickedEventArgs} from "@glideapps/glide-data-grid/dist/ts/data-grid/data-grid-types";
import {
  setDispatchRequestPageFilter,
  setDispatchRequestPageStateCell,
  setDispatchRequestPageStateSort,
  setDispatchRequestPageStateWidths
} from "../../store/dispatch-request-page/actions";
import {notifications} from "@mantine/notifications";

export type DispatchRequestPageProps = {}

export const DispatchRequestPage = (props: DispatchRequestPageProps) => {
  const {} = props

  const [selectedDispatchRequest, setSelectedDispatchRequest] = useState<DispatchRequestEntity | undefined>()

  const dispatch = useDispatch();
  const {regionId, appCategory} = useApp();
  const {t} = useTranslation(["common", "translation"])
  const rejectDisclosure = useDisclosure(false)
  const [filterOpened, filterHandler] = useDisclosure(false);
  const navigate = useNavigate()

  const appDttm = useAppSelector(getAppDttm);
  const filter = useAppSelector(getDispatchRequestPageFilter);
  const gridState = useAppSelector(getDispatchRequestPageState)

  const dataEditorRef = useRef<DataEditorRef>(null);

  const [scrollTimerReference, setScrollTimerReference] = useState<NodeJS.Timeout | undefined>(undefined);
  const [showSearch, setShowSearch] = useState(false);
  const onSearchClose = useCallback(() => setShowSearch(false), []);

  const [selection, setSelection] = useState<GridSelection>({
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty()
  });

  const [colWidths, setColWidths] = useState<(number | undefined)[]>(gridState.widths)
  const [sort, setSort] = useState<GridSort | undefined>(gridState.sort)

  const selectDispatchRequests = useMemo(() => {
    return dispatchRequestsSelector();
  }, []);

  const prefetchDispatchRequests = useDispatchRequestPrefetch("searchDispatchRequests");

  const {dispatchRequests, isFetching, error, refetch} = useSearchDispatchRequestsQuery({
    fromDttm: adjustToTimeZone(startOfDay(appDttm)).getTime(),
    toDttm: adjustToTimeZone(endOfDay(appDttm)).getTime(),
    regionId: regionId
  }, {
    pollingInterval: 120000,
    refetchOnMountOrArgChange: 120,
    selectFromResult: (result) => ({
      ...result,
      dispatchRequests: selectDispatchRequests(result, {
        regionId: regionId,
        category: appCategory,
        pickupSiteId: getObjectId(filter.pickupSite),
        deliverySiteId: getObjectId(filter.deliverySite),
        freightTypeId: getObjectId(filter.freightType),
      }),
      isFetching: result.isFetching,
      error: result.error ? extractApiError(result.error) : undefined
    })
  });

  const prefetchDttmRange = useCallback((date: Date) => {
    const rootFromDate = startOfDay(date);
    const rootToDate = endOfDay(date);

    if (regionId) {
      // prefetch the previous day
      prefetchDispatchRequests({
        fromDttm: adjustToTimeZone(subDays(rootFromDate, 1)).getTime(),
        toDttm: adjustToTimeZone(subDays(rootToDate, 1)).getTime(),
        regionId: regionId,
        categories: appCategory ? [appCategory.code] : undefined,
      });

      // prefetch the next day
      prefetchDispatchRequests({
        fromDttm: adjustToTimeZone(addDays(rootFromDate, 1)).getTime(),
        toDttm: adjustToTimeZone(addDays(rootToDate, 1)).getTime(),
        regionId: regionId,
        categories: appCategory ? [appCategory.code] : undefined,
      });
    }

  }, [prefetchDispatchRequests, regionId, appCategory]);

  const onDateChange = useCallback((date: Date) => {
    dispatch(setAppDate(date.getTime()));
    prefetchDttmRange(date);
  }, [dispatch, prefetchDttmRange]);

  const {
    rejectDispatchRequest,
    isRejecting
  } = useDispatchRequestMutation()

  const onActionClick = useCallback((data: DispatchRequestEntity, action: DispatchRequestDispatchAction) => {
    if (action === 'ACCEPT') {
      navigate(`/requests/accept`, {state: {id: data.id}})
    } else if (action === 'REJECT') {
      rejectDisclosure[1].open()
    } else if (action === 'NEW_DISPATCH') {
      navigate(`/requests/add-dispatch`, {state: {id: data.id}})
    }

    setSelectedDispatchRequest(data)
  }, [rejectDisclosure, navigate])

  const onRejectSubmit = useCallback((comments: string | undefined) => {
    if (selectedDispatchRequest && comments) {
      rejectDispatchRequest(selectedDispatchRequest.id, comments, undefined)
      rejectDisclosure[1].close()
    }
  }, [rejectDispatchRequest, selectedDispatchRequest, rejectDisclosure])

  useEffect(() => {
    setScrollTimerReference(
      setTimeout(() => {
        if (gridState?.cell) {
          dataEditorRef?.current?.scrollTo(0, gridState.cell[1], "both", 0, 0, {
            vAlign: "start",
            hAlign: "start"
          });
          setSelection(prevState => gridState.cell ? ({
            ...prevState,
            current: {
              cell: [0, gridState.cell[1]],
              range: {
                x: 0,
                y: gridState.cell[1],
                width: 1,
                height: 1
              },
              rangeStack: []
            }
          }) : ({...prevState}));
        }
      }, 200)
    );

    return () => {
      clearTimeout(scrollTimerReference)
    }
  }, []);

  const onFilterSubmit = useCallback((filter: DispatchRequestFilterFormValues) => {
    dispatch(setDispatchRequestPageFilter(filter))
    filterHandler.close()
  }, [dispatch, filterHandler])

  const onCellClicked = useCallback((cell: Item, event: CellClickedEventArgs) => {
    dispatch(setDispatchRequestPageStateCell([cell[0], cell[1]]))
  }, [dispatch])

  useEffect(() => {
    return () => {
      dispatch(setDispatchRequestPageStateSort(sort))
    }
  }, [sort, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setDispatchRequestPageStateWidths(colWidths))
    }
  }, [colWidths, dispatch]);

  useEffect(() => {
    if (error) {
      notifications.show({
        id: "dispatchRequestPage_error",
        color: "red",
        title: t("common.error", "Error"),
        message: error.localized ?? t("common.error", "Error"),
        autoClose: 4000,
      })
    }
  }, [error, t]);

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Flex direction="column" h="100%" m="sm" gap="sm">
                <Card shadow="sm" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Group>
                      <Title order={4}>
                        {t("page.dispatchrequests.title", "Dispatch Requests", {ns: "translation"})}
                      </Title>
                      <NavHeaderIsland/>
                    </Group>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t("common.action.plural", "Actions", {ns: "common"})}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("common.action.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <DispatchRequestToolbar
                    date={new Date(appDttm)}
                    onDateChange={onDateChange}
                    showSearch={showSearch}
                    onSearchClick={() => setShowSearch(!showSearch)}
                    filter={filter}
                    onFilterClick={() => filterHandler.open()}/>
                </Card>
                <LoadingOverlay visible={isFetching} overlayBlur={2}/>
                <Box sx={{flex: 1, overflow: 'auto'}}>
                  <DispatchRequestDataGrid
                    ref={dataEditorRef}
                    height="100%"
                    showSearch={showSearch}
                    onSearchClose={onSearchClose}
                    onCellClicked={onCellClicked}
                    gridSelection={selection}
                    onGridSelectionChange={setSelection}
                    colWidths={gridState.widths}
                    onColumnResize={setColWidths}
                    sort={gridState.sort}
                    onSort={setSort}
                    data={dispatchRequests}
                    onActionClick={onActionClick}/>
                </Box>
              </Flex>

              <DispatchRequestFilterFormModal
                title={t<string>("common.action.filter", "Filter", {ns: "common"})}
                opened={filterOpened}
                onClose={filterHandler.close}
                onSubmit={onFilterSubmit}
                defaultValues={{
                  ...filter
                }}
              />

              <CommentModal
                title={t<string>("profile.dispatchrequest.action.reject.alert.title", "Reject")}
                opened={rejectDisclosure[0]}
                onClose={rejectDisclosure[1].close}
                onSubmit={onRejectSubmit}
                loading={isRejecting}
                color="red"
                message={
                  <Text size="sm">
                    {t("profile.dispatchrequest.action.reject.alert.message", "Are you sure you want to reject this dispatch request?")}
                  </Text>
                }/>

            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};

